/**
 * Contract and invoices for one customer.
 *
 * ACH is what an invoice leads with and the panel says so, because the
 * difference is $5 against $87 on a $2,995 contract and somebody choosing
 * "card" out of habit costs real money.
 *
 * NOTHING HERE GATES ACCESS. An overdue invoice shows as overdue and does
 * nothing else: these are the documents a nurse opens at 3am, and an
 * accounts-receivable problem is a phone call, not a lock.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../lib/callable';
import { formatDayUS } from '../../shared/dates';
import page from '../../shared/Page.module.css';
import fields from '../../shared/Form.module.css';
import own from './Console.module.css';
import { GroupCard } from '../../shared/GroupCard';

interface StripeStatus {
  configured: boolean;
  mode: 'test' | 'live' | null;
  reason?: string;
  webhookConfigured?: boolean;
}

interface Invoice {
  stripeInvoiceId: string;
  number: string | null;
  status: string | null;
  amountDue: number;
  amountPaid: number;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  dueDate: string | null;
  paidAt: string | null;
  /** When it was emailed. Stripe says "open" either way, so this is how "not sent yet" shows. */
  sentAt?: string | null;
  description?: string | null;
  daysOverdue?: number;
}

/** Where an invoice is emailed: the Billing contact person, or an older contract field. */
interface BillingRecipient {
  name: string | null;
  email: string;
  source: 'billingContact' | 'contract';
}

interface Billing {
  contract: {
    plan: string; annualAmount: number; termStart: string; termEnd: string;
    poNumber?: string | null; billingContactEmail?: string | null;
    facilityCount?: number | null;
    implementationFee?: number | null; implementationWaived?: boolean | null;
  } | null;
  invoices: Invoice[];
  outstandingAmount: number;
  billingRecipient?: BillingRecipient | null;
  listPrice: number | null;
  includedLocations: number | null;
}

/**
 * The plans, and what each includes.
 *
 * Mirrors functions/src/billing/plans.ts, which mirrors the price list on the
 * marketing site. Locations is the only thing that scales: staff and readers
 * are unlimited on every plan and must never be presented as a reason to move
 * up a tier.
 */
const PLANS = [
  { key: 'clinic', label: 'Clinic', price: 1995, locations: 1 },
  { key: 'professional', label: 'Professional', price: 2995, locations: 5 },
  { key: 'organization', label: 'Organization', price: 5995, locations: 15 },
  { key: 'custom', label: 'Custom', price: null, locations: null },
] as const;

const money = (n: number) => `$${n.toLocaleString('en-US')}`;
/*
 * `new Date('2026-10-15')` parses as UTC midnight and renders in local time, so
 * every term start and end displayed a day early for anybody west of UTC.
 * formatDayUS splits the string instead: a date with no time in it has no time
 * zone to be converted between.
 */
const day = formatDayUS;

export function BillingPanel({ orgId, onEditContacts }: {
  orgId: string;
  /** Opens the Contacts stage, where the Billing contact is recorded. */
  onEditContacts?: () => void;
}) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    plan: 'professional', facilityCount: '5', annualAmount: '2995',
    termStart: '', termEnd: '', poNumber: '', implementationFee: '', implementationWaived: false,
  });

  const stripe = useQuery({
    queryKey: ['stripe-status'],
    queryFn: () => call<Record<string, never>, StripeStatus>('sysAdminGetStripeStatus', {}),
  });

  const billing = useQuery({
    queryKey: ['billing', orgId],
    queryFn: () => call<{ orgId: string }, Billing>('sysAdminGetOrgBilling', { orgId }),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['billing', orgId] });
    void queryClient.invalidateQueries({ queryKey: ['go-live'] });
  };

  const createInvoice = useMutation({
    mutationFn: (input: { amount: number; description: string; poNumber?: string; send: boolean }) =>
      call('sysAdminCreateStripeInvoice', { orgId, ...input }),
    onSuccess: () => { setAmount(''); setDescription(''); setPoNumber(''); refresh(); },
  });
  const sendOne = useMutation({
    mutationFn: (invoiceId: string) => call('sysAdminSendStripeInvoice', { orgId, invoiceId }),
    onSuccess: refresh,
  });
  const markPaid = useMutation({
    mutationFn: (invoiceId: string) => call('sysAdminMarkInvoicePaidOutOfBand', { orgId, invoiceId }),
    onSuccess: refresh,
  });

  /**
   * Recording what was agreed.
   *
   * The callable existed and nothing called it: the panel said "No contract on
   * file. Record one before invoicing" and offered no way to record one, so the
   * Order Form — which takes its plan, locations, price and term from here —
   * could never be filled.
   */
  const saveContract = useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      call('sysAdminSaveOrgContract', { orgId, ...input }),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['billing', orgId] });
      // The Order Form and the go-live checklist both read the contract.
      void queryClient.invalidateQueries({ queryKey: ['contract-pack', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['go-live'] });
    },
  });

  const busy = createInvoice.isPending || sendOne.isPending || markPaid.isPending
    || saveContract.isPending;
  const recipient = billing.data?.billingRecipient ?? null;
  const billingEmail = recipient?.email ?? null;
  const ready = Boolean(!busy && amount && description.trim() && stripe.data?.configured);
  const failure = [createInvoice.error, sendOne.error, markPaid.error,
    saveContract.error].find(Boolean);

  /** Open the form on what is already agreed, so an edit is not a retype. */
  const openContract = () => {
    const c = data?.contract;
    setForm({
      plan: c?.plan ?? 'professional',
      facilityCount: String(c?.facilityCount ?? 5),
      annualAmount: String(c?.annualAmount ?? 2995),
      termStart: c?.termStart?.slice(0, 10) ?? '',
      termEnd: c?.termEnd?.slice(0, 10) ?? '',
      poNumber: c?.poNumber ?? '',
      implementationFee: c?.implementationFee ? String(c.implementationFee) : '',
      implementationWaived: c?.implementationWaived ?? false,
    });
    setEditing(true);
  };

  /** A plan carries a price and a location count; both stay editable. */
  const choosePlan = (key: string) => {
    const plan = PLANS.find((p) => p.key === key);
    setForm((f) => ({
      ...f,
      plan: key,
      annualAmount: plan?.price ? String(plan.price) : f.annualAmount,
      facilityCount: plan?.locations ? String(plan.locations) : f.facilityCount,
    }));
  };

  const contractValid = form.termStart !== '' && form.termEnd !== ''
    && form.termEnd > form.termStart
    && Number(form.facilityCount) >= 1 && Number(form.annualAmount) >= 0;
  const data = billing.data;

  return (
    // No heading of its own: the stage is already titled Billing. Each part
    // below is its own group.
    <section aria-label="Billing details">

      {/*
        No role="status" on either of these. A live region is for something
        that CHANGES — the notice after an action — and both of these are on
        the page at first paint. Announcing them competes with the ones that
        are actually news, and made "the resend said so" ambiguous.
      */}
      {stripe.data && !stripe.data.configured && (
        <div className={`${page.notice} ${page.noticeWarn}`}>
          Stripe is not connected, so no invoice can be issued. {stripe.data.reason}
        </div>
      )}
      {stripe.data?.configured && stripe.data.mode === 'test' && (
        <div className={`${page.notice} ${page.noticeInfo}`}>
          Stripe is in <strong>test mode</strong>. Invoices are real objects in Stripe and no money moves.
          {stripe.data.webhookConfigured === false && ' The webhook secret is not set, so payment status will not come back on its own.'}
        </div>
      )}

      <GroupCard title="What they agreed to">
      {editing ? (
        <div>
          <p className={page.itemMeta} style={{ marginBottom: 'var(--sp-3)' }}>
            The Order Form takes its plan, locations, price and term from here, so this is
            recorded before that document can be sent.
          </p>
          <div className={own.invoiceForm}>
            <div>
              <label className={fields.label} htmlFor="c-plan">Plan</label>
              <select id="c-plan" className={fields.select} value={form.plan}
                onChange={(e) => choosePlan(e.target.value)}>
                {PLANS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}{p.price ? ` — ${money(p.price)}, up to ${p.locations} locations` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={fields.label} htmlFor="c-locations">Locations</label>
              <input id="c-locations" className={fields.input} type="number" min={1} max={500}
                value={form.facilityCount}
                onChange={(e) => setForm({ ...form, facilityCount: e.target.value })} />
            </div>
            <div>
              <label className={fields.label} htmlFor="c-amount">Agreed annual price (whole dollars)</label>
              <input id="c-amount" className={fields.input} type="number" min={0} max={100000000}
                value={form.annualAmount}
                onChange={(e) => setForm({ ...form, annualAmount: e.target.value })} />
            </div>
            <div>
              {/* Distinct from the invoice's own PO field below: two controls
                  labeled "PO number" on one panel is ambiguous to anybody
                  reading the page by its labels. */}
              <label className={fields.label} htmlFor="c-po">
                Their purchase order number <span className={page.absent}>optional</span>
              </label>
              <input id="c-po" className={fields.input} maxLength={100} value={form.poNumber}
                onChange={(e) => setForm({ ...form, poNumber: e.target.value })} />
            </div>
            <div>
              <label className={fields.label} htmlFor="c-start">Term starts</label>
              <input id="c-start" className={fields.input} type="date" value={form.termStart}
                onChange={(e) => setForm({ ...form, termStart: e.target.value })} />
            </div>
            <div>
              <label className={fields.label} htmlFor="c-end">Term ends</label>
              <input id="c-end" className={fields.input} type="date" value={form.termEnd}
                onChange={(e) => setForm({ ...form, termEnd: e.target.value })} />
            </div>
            <div>
              <label className={fields.label} htmlFor="c-impl">
                Migration fee <span className={page.absent}>optional</span>
              </label>
              <input id="c-impl" className={fields.input} type="number" min={0} max={1000000}
                disabled={form.implementationWaived} value={form.implementationFee}
                onChange={(e) => setForm({ ...form, implementationFee: e.target.value })} />
            </div>
            <div className={own.checkRow} style={{ alignSelf: 'end' }}>
              <input id="c-waived" type="checkbox" checked={form.implementationWaived}
                onChange={(e) => setForm({ ...form, implementationWaived: e.target.checked })} />
              <label htmlFor="c-waived" className={page.itemMeta}>Migration help waived</label>
            </div>
          </div>
          {form.termStart !== '' && form.termEnd !== '' && form.termEnd <= form.termStart && (
            <p className={`${page.itemMeta} ${page.warnText}`}>The term has to end after it starts.</p>
          )}
          <div className={page.actions} style={{ marginTop: 'var(--sp-3)' }}>
            <button type="button" className={`${page.btn} ${page.btnPrimary}`}
              disabled={busy || !contractValid}
              onClick={() => saveContract.mutate({
                plan: form.plan,
                facilityCount: Number(form.facilityCount),
                annualAmount: Number(form.annualAmount),
                termStart: form.termStart,
                termEnd: form.termEnd,
                poNumber: form.poNumber.trim() || undefined,
                implementationFee: form.implementationWaived
                  ? 0 : Number(form.implementationFee || 0),
                implementationWaived: form.implementationWaived,
              })}>
              {saveContract.isPending ? 'Saving…' : 'Save the contract'}
            </button>
            <button type="button" className={page.btn} disabled={busy}
              onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      ) : data?.contract ? (
        <dl className={page.facts}>
          <div><dt className={page.factTerm}>Plan</dt><dd className={page.factValue}>{data.contract.plan}{data.includedLocations ? ` · up to ${data.includedLocations} locations` : ''}</dd></div>
          <div>
            <dt className={page.factTerm}>Agreed</dt>
            <dd className={page.factValue}>
              {money(data.contract.annualAmount)} a year
              {data.listPrice && data.listPrice !== data.contract.annualAmount
                ? ` · list ${money(data.listPrice)}` : ''}
            </dd>
          </div>
          <div><dt className={page.factTerm}>Term</dt><dd className={page.factValue}>{day(data.contract.termStart)} to {day(data.contract.termEnd)}</dd></div>
          {data.contract.poNumber && <div><dt className={page.factTerm}>PO</dt><dd className={page.factValue}>{data.contract.poNumber}</dd></div>}
          <div><dt className={page.factTerm}>Outstanding</dt><dd className={page.factValue}>{money(data.outstandingAmount)}</dd></div>
          <div>
            <dt className={page.factTerm}> </dt>
            <dd className={page.factValue}>
              <button type="button" className={page.linkBtn} onClick={openContract}>
                Change what was agreed
              </button>
            </dd>
          </div>
        </dl>
      ) : (
        <>
          <p className={page.absent}>
            No contract on file. The Order Form needs one, and so does invoicing.
          </p>
          <div className={page.actions}>
            <button type="button" id="billing-record-contract" className={`${page.btn} ${page.btnPrimary}`} onClick={openContract}>
              Record the contract
            </button>
          </div>
        </>
      )}
      </GroupCard>

      {failure && (
        <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
          {isCallFailure(failure) ? failure.message : failure instanceof Error ? failure.message : 'That did not work.'}
        </div>
      )}

      <GroupCard title="New invoice">
      <p className={page.itemMeta} style={{ marginBottom: 'var(--sp-3)' }}>
        Sent for payment on net 30, offering <strong>bank transfer first</strong> and card second —
        ACH costs $5 where a card costs about 3%. A check can be recorded against it later.
      </p>
      {/* Each label with its own input in one cell. As loose siblings the grid
          put labels and boxes in separate cells, and they drifted apart. */}
      <div className={own.invoiceForm}>
        <div>
          <label className={fields.label} htmlFor="inv-amount">Amount (whole dollars)</label>
          {/* Seven digits: the backend caps an invoice at $1,000,000, and a
              field that accepts more only sets up a refusal after the typing. */}
          <input id="inv-amount" className={fields.input} inputMode="numeric" maxLength={7} value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, '').slice(0, 7))} disabled={busy} />
        </div>
        <div>
          <label className={fields.label} htmlFor="inv-desc">What it is for</label>
          <input id="inv-desc" className={fields.input} maxLength={500} value={description}
            onChange={(e) => setDescription(e.target.value)} disabled={busy} />
        </div>
        <div>
          <label className={fields.label} htmlFor="inv-po">PO number <span className={fields.optional}>optional</span></label>
          <input id="inv-po" className={fields.input} maxLength={100} value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)} disabled={busy} />
        </div>
      </div>
      {/*
        SENDING NEEDS SOMEWHERE TO SEND IT. Stripe refuses to email an invoice
        to a customer with no email address, and the contract's billing
        contact is where that comes from. Saying so here beats a Stripe error
        after the invoice already exists.
      */}
      {billing.data && (
        <p className={page.itemMeta} style={{ marginTop: 'var(--sp-2)' }}>
          {recipient ? (
            <>
              Invoices are emailed to <strong>{recipient.name ? `${recipient.name}, ` : ''}{recipient.email}</strong>
              {recipient.source === 'billingContact' ? ', their Billing contact.' : ', from the contract.'}
            </>
          ) : (
            <span className={page.warnText}>
              No Billing contact, so an invoice can be created but not sent.
            </span>
          )}{' '}
          {onEditContacts && (
            <button type="button" className={page.linkBtn} onClick={onEditContacts}>
              {recipient ? 'Change the Billing contact' : 'Add a Billing contact'}
            </button>
          )}
        </p>
      )}
      <div className={page.actions} style={{ marginTop: 'var(--sp-3)' }}>
        <button type="button" className={page.btn}
          disabled={!ready}
          onClick={() => createInvoice.mutate({ amount: Number(amount), description: description.trim(), poNumber: poNumber.trim() || undefined, send: false })}>
          Create, do not send
        </button>
        <button type="button" className={`${page.btn} ${page.btnPrimary}`}
          disabled={!ready || !billingEmail}
          onClick={() => createInvoice.mutate({ amount: Number(amount), description: description.trim(), poNumber: poNumber.trim() || undefined, send: true })}>
          Create and send
        </button>
      </div>

      </GroupCard>

      <GroupCard title="Invoices" meta={data?.invoices?.length ? `${data.invoices.length}` : undefined}>
      {!data?.invoices?.length ? (
        <p className={page.absent}>None yet.</p>
      ) : (
        <ul className={page.list} aria-label="Invoices">
          {data.invoices.map((inv) => (
            <li key={inv.stripeInvoiceId} className={page.listItem}>
              <div>
                <div className={page.itemName}>
                  {inv.number ?? inv.stripeInvoiceId} · {money(inv.amountDue)}
                  <span className={`${page.tag} ${inv.status === 'paid' ? page.ok : inv.status === 'open' ? page.warn : page.neutral}`} style={{ marginLeft: 8 }}>
                    {inv.status ?? 'unknown'}
                  </span>
                </div>
                <div className={page.itemMeta}>
                  {inv.description && <>{inv.description}. </>}
                  {inv.status === 'paid' && inv.paidAt ? `Paid ${day(inv.paidAt)}.`
                    : inv.dueDate ? `Due ${day(inv.dueDate)}.` : 'No due date.'}
                  {inv.status === 'open' && (inv.sentAt ? ` Sent ${day(inv.sentAt)}.` : ' Not sent yet.')}
                  {inv.daysOverdue ? <span className={page.dangerText}> {inv.daysOverdue} days overdue.</span> : null}
                  {inv.hostedInvoiceUrl && <> <a href={inv.hostedInvoiceUrl} target="_blank" rel="noreferrer">Open in Stripe</a></>}
                  {inv.invoicePdf && <> · <a href={inv.invoicePdf} target="_blank" rel="noreferrer">PDF</a></>}
                </div>
              </div>
              <div className={page.actions}>
                {/* Created and not emailed yet. Stripe finalizes on create, so
                    this is an OPEN invoice with no sentAt — never a draft. */}
                {inv.status === 'open' && !inv.sentAt && (
                  <button type="button" className={page.btn} disabled={busy || !billingEmail}
                    title={billingEmail ? undefined : 'No Billing contact to send it to'}
                    onClick={() => sendOne.mutate(inv.stripeInvoiceId)}>Send</button>
                )}
                {inv.status === 'open' && (
                  <button type="button" className={page.btn} disabled={busy}
                    onClick={() => markPaid.mutate(inv.stripeInvoiceId)}>A check arrived</button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      </GroupCard>
    </section>
  );
}
